import { useEffect, useState } from "react";
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router";
import { Button } from "@progress/kendo-react-buttons";
import {
  Notification,
  NotificationGroup,
} from "@progress/kendo-react-notification";
import { useCurrentUser } from "src/dm/auth";
import { useDealDetail, isDealForbidden } from "src/dm/hooks/useDeals";
import {
  openPrintReport,
  useBatchValidate,
  useChangeStatus,
  useCopyDeal,
  useReturnToVendor,
  useSetLock,
  useSetTermSheet,
  useValidateDeal,
} from "src/dm/hooks/useDealActions";
import { useDealItems } from "src/dm/hooks/useDealItems";
import { useDealNotes } from "src/dm/hooks/useDealNotes";
import { useDealReward } from "src/dm/hooks/useDealRewards";
import { useDealPromoGroups } from "src/dm/hooks/useDealPromoGroups";
import { useDealAttachments } from "src/dm/hooks/useAttachments";
import UserName from "src/dm/components/UserName/UserName";
import {
  useDealErrors,
  useDealFees,
  useDealHistory,
  useDealSignatures,
} from "src/dm/hooks/useDealActivity";
import { DealDetail } from "src/dm/interfaces/Deal";
import * as s from "./DealLayout.css";

const fmtDate = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
};

const fmtText = (v: string | null | undefined): string =>
  v == null || v === "" ? "—" : v;

const DealHeader = ({ deal }: { deal: DealDetail }) => {
  // "Edit" is contextual to the Header sub-page only — opens the header
  // form editor. Other sub-pages (Items / Reward / Notes / etc.) have
  // their own editing UI, so the button hides on those routes.
  const location = useLocation();
  const isHeaderRoute = location.pathname === `/dm/deals/${deal.dealId}`;
  return (
    <div className={s.headerBar}>
      <div className={s.dealId}>{deal.dealId}</div>

      <div className={s.headerMeta}>
        <div>
          <div className={s.metaKey}>Description</div>
          <div className={s.metaVal}>{fmtText(deal.description)}</div>
        </div>
        <div>
          <div className={s.metaKey}>Banner</div>
          <div className={s.metaVal}>{fmtText(deal.bannerCode)}</div>
        </div>
        <div>
          <div className={s.metaKey}>Cat Mgr</div>
          <div className={s.metaVal}>{deal.categoryManagerCode ?? "—"}</div>
        </div>
        <div>
          <div className={s.metaKey}>Owner</div>
          {/* Resolve DOMAIN\username → person name via the shared
              UserName component (audit/21-visual-diff §12). The raw
              userId is preserved in the hover tooltip for power users. */}
          <div className={s.metaVal}>
            <UserName userId={deal.ownerUserId} />
          </div>
        </div>
        <div>
          <div className={s.metaKey}>Ad Start</div>
          <div className={s.metaVal}>{fmtDate(deal.adStartDate)}</div>
        </div>
        <div>
          <div className={s.metaKey}>Ad End</div>
          <div className={s.metaVal}>{fmtDate(deal.adEndDate)}</div>
        </div>
      </div>

      {/* Edit + status/locked pills. All other action buttons live in the
          sidebar ActionsPanel below Contents (legacy ContentsAndActions
          left-rail parity). Edit is the lone exception — placed on the
          header bar where it reads as a primary action on the screen the
          user is currently viewing. */}
      <div className={s.headerActions}>
        {isHeaderRoute && (
          <Link to={`/dm/deals/${deal.dealId}/edit`} className={s.headerEditButton}>
            Edit
          </Link>
        )}
        <span className={s.statusPill}>{fmtText(deal.status)}</span>
        {deal.isLocked && (
          <span
            title="This deal is locked"
            style={{
              background: "#fde68a",
              color: "#92400e",
              padding: "0.2rem 0.5rem",
              borderRadius: "999px",
              fontSize: "0.7rem",
              fontWeight: 600,
              textTransform: "uppercase",
            }}
          >
            Locked
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * Action buttons rendered in the left sidebar below the Contents nav.
 * Mirrors legacy `ContentsAndActions.ascx` where Contents + Actions are
 * a single vertical stack on the left side of the deal page.
 */
const ActionsPanel = ({ deal }: { deal: DealDetail }) => {
  const changeStatus = useChangeStatus(deal.dealId);
  const copy = useCopyDeal(deal.dealId);
  const setLock = useSetLock(deal.dealId);
  const setTermSheet = useSetTermSheet(deal.dealId);
  const validate = useValidateDeal(deal.dealId);
  const batchValidate = useBatchValidate(deal.dealId);
  const returnToVendor = useReturnToVendor(deal.dealId);
  const navigate = useNavigate();

  const runStatusChange = async (target: string, label: string) => {
    try {
      const result = await changeStatus.mutateAsync({ status: target });
      alert(
        `${label} succeeded.\nValidated against: ${result.validatedAgainst}\nError count: ${result.errorCount}`
      );
    } catch (e) {
      const status = (e as { response?: { status?: number } }).response?.status;
      const message = e instanceof Error ? e.message : "unknown error";
      if (status === 409) {
        const ok = confirm(
          `Validation found errors. ${message}\n\nClick OK to ${label.toLowerCase()} anyway (force=true).`
        );
        if (ok) {
          await changeStatus.mutateAsync({ status: target, force: true });
        }
      } else {
        alert(`${label} failed: ${message}`);
      }
    }
  };

  // The Copy button used to POST .../copy directly with an empty body,
  // skipping the legacy CopyDeal.aspx destination-picker step entirely
  // (audit 22-screenshot-inventory.md MAJOR regression). We now route to
  // the dedicated /deals/:dealId/copy page which pre-fills with the
  // source deal's editable fields, lets the user override anything before
  // submit, and toggles the chkCopyAttachments checkbox. The actual
  // POST happens from that page via useCopyDealWithOverrides.
  const handleCopy = () => {
    navigate(`/dm/deals/${deal.dealId}/copy`);
  };
  const handleSubmit = async () => {
    if (!confirm(`Submit deal ${deal.dealId} for approval?`)) return;
    await runStatusChange("SUBMIT", "Submit");
  };
  const handleApprove = async () => {
    if (!confirm(`Approve deal ${deal.dealId}?`)) return;
    await runStatusChange("APPROVE", "Approve");
  };
  const handleReject = async () => {
    if (!confirm(`Reject deal ${deal.dealId}?`)) return;
    await runStatusChange("REJECT", "Reject");
  };
  const handleEnd = async () => {
    if (!confirm(`End deal ${deal.dealId}?`)) return;
    await runStatusChange("COMPLETE", "End");
  };
  const handleLockToggle = async () => {
    await setLock.mutateAsync(!deal.isLocked);
  };
  const handleValidate = async () => {
    try {
      const result = await validate.mutateAsync();
      alert(
        `Validated for next-status transition: ${result.targetStatus}\nErrors: ${result.errorCount} • Notes: ${result.noteCount}` +
          (result.plsqlError ? `\nPL/SQL error: ${result.plsqlError}` : "")
      );
    } catch (e) {
      alert(`Validate failed: ${e instanceof Error ? e.message : "unknown"}`);
    }
  };
  const handleBatchValidate = async () => {
    try {
      const result = await batchValidate.mutateAsync();
      alert(
        `Batch validation queued for ${result.targetStatus}.\n${result.note}` +
          (result.errorCount ? `\nErrors written so far: ${result.errorCount}` : "") +
          (result.plsqlError ? `\nPL/SQL error: ${result.plsqlError}` : "")
      );
    } catch (e) {
      alert(`Batch Validate failed: ${e instanceof Error ? e.message : "unknown"}`);
    }
  };
  const handlePrint = async () => {
    await openPrintReport(deal.dealId, "detail");
  };
  const handlePrintSummary = async () => {
    await openPrintReport(deal.dealId, "summary");
  };
  const handlePrintOverview = async () => {
    await openPrintReport(deal.dealId, "overview");
  };
  const handleReturnToVendor = async () => {
    if (!confirm(`Return deal ${deal.dealId} to vendor?`)) return;
    try {
      await returnToVendor.mutateAsync({});
    } catch (e) {
      const status = (e as { response?: { status?: number } }).response?.status;
      if (status === 409) {
        const ok = confirm(
          `Validation found errors. Click OK to return to vendor anyway (force=true).`
        );
        if (ok) await returnToVendor.mutateAsync({ force: true });
      } else {
        alert(`Return To Vendor failed: ${e instanceof Error ? e.message : "unknown"}`);
      }
    }
  };
  // audit/22-screenshot-inventory.md §4 MINOR #6 — the Term Sheet
  // checkbox auto-saves silently in the sidebar action panel. Legacy had
  // an explicit "Save" button so the action was visibly confirmed. We
  // surface a transient Kendo Notification toast instead so the user
  // gets feedback that the flag was updated server-side. Auto-dismisses
  // after 2.5s.
  const [termSheetToast, setTermSheetToast] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  useEffect(() => {
    if (!termSheetToast) return;
    const t = setTimeout(() => setTermSheetToast(null), 2500);
    return () => clearTimeout(t);
  }, [termSheetToast]);
  const handleTermSheetToggle = async () => {
    const target = !deal.isTermSheet;
    try {
      await setTermSheet.mutateAsync(target);
      setTermSheetToast({
        type: "success",
        text: `Term Sheet flag ${target ? "set" : "cleared"}.`,
      });
    } catch (e) {
      setTermSheetToast({
        type: "error",
        text: `Term Sheet update failed: ${
          e instanceof Error ? e.message : "unknown"
        }`,
      });
    }
  };

  const isNew = deal.status === "NEW";
  const isSubmitted = deal.status === "SUBMIT" || deal.status === "SUBMITTED";
  const isComplete = deal.status === "COMPLETE" || deal.status === "COMPLETED";
  const pending =
    // copy is no longer a pending mutation here (the Copy button just
    // navigates to /deals/:id/copy); we still surface other long-running
    // mutations so the action panel disables in lock-step.
    copy.isPending ||
    changeStatus.isPending ||
    validate.isPending ||
    batchValidate.isPending ||
    setLock.isPending ||
    setTermSheet.isPending ||
    returnToVendor.isPending;

  return (
    <>
      <h3 className={s.actionsHeading}>Actions</h3>
      <div className={s.actionsList}>
        <Button type="button" size="small" className={s.actionButton} onClick={handleValidate} disabled={pending}>
          {validate.isPending ? "Validating…" : "Validate"}
        </Button>
        <Button type="button" size="small" className={s.actionButton} onClick={handleBatchValidate} disabled={pending} title="Validate offline; legacy emails the results">
          {batchValidate.isPending ? "Queuing…" : "Batch Validate"}
        </Button>
        {isNew && (
          <Button type="button" size="small" themeColor="primary" className={s.actionButton} onClick={handleSubmit} disabled={pending}>
            Submit
          </Button>
        )}
        {isSubmitted && (
          <>
            <Button type="button" size="small" themeColor="success" className={s.actionButton} onClick={handleApprove} disabled={pending}>
              Approve
            </Button>
            <Button type="button" size="small" themeColor="warning" className={s.actionButton} onClick={handleReject} disabled={pending}>
              Reject
            </Button>
          </>
        )}
        <Button type="button" size="small" className={s.actionButton} onClick={handleLockToggle} disabled={pending}>
          {deal.isLocked ? "Unlock" : "Lock"}
        </Button>
        <Button type="button" size="small" className={s.actionButton} onClick={handleCopy} disabled={pending} title="Open the Copy Deal destination picker">
          Copy
        </Button>
        <Button type="button" size="small" className={s.actionButton} onClick={handlePrint} disabled={pending} title="Open Deal Print Screen (SSRS)">
          Print Detail
        </Button>
        <Button type="button" size="small" className={s.actionButton} onClick={handlePrintSummary} disabled={pending} title="Open Deal Print Screen Summary (SSRS)">
          Summary
        </Button>
        <Button type="button" size="small" className={s.actionButton} onClick={handlePrintOverview} disabled={pending} title="Open Deal Print Screen Overview (SSRS)">
          Overview
        </Button>
        <Button type="button" size="small" themeColor="warning" className={s.actionButton} onClick={handleReturnToVendor} disabled={pending || isComplete} title="Return to vendor for modification (status → MODIFY)">
          Return To Vendor
        </Button>
        <label className={s.actionInlineToggle} title="Quick-toggle TERM_SHEET_FG">
          <input
            type="checkbox"
            checked={deal.isTermSheet}
            disabled={setTermSheet.isPending}
            onChange={handleTermSheetToggle}
          />
          Term Sheet
        </label>
        <Button type="button" size="small" themeColor="error" className={s.actionButton} onClick={handleEnd} disabled={pending || isComplete}>
          End
        </Button>
      </div>
      <NotificationGroup
        style={{
          position: "fixed",
          right: "1rem",
          bottom: "1rem",
          zIndex: 10000,
        }}
      >
        {termSheetToast && (
          <Notification
            type={{ style: termSheetToast.type, icon: true }}
            closable
            onClose={() => setTermSheetToast(null)}
          >
            <span>{termSheetToast.text}</span>
          </Notification>
        )}
      </NotificationGroup>
    </>
  );
};

interface NavItemProps {
  to: string;
  label: string;
  count?: number;
  /** Render a "configured" dot instead of a count badge. */
  dot?: boolean;
  end?: boolean;
}
const NavItem = ({ to, label, count, dot, end }: NavItemProps) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) => `${s.navLink}${isActive ? " active" : ""}`}
  >
    <span>{label}</span>
    {count !== undefined && <span className={s.badge}>{count}</span>}
    {dot && (
      <span
        title="Configured"
        aria-label="Configured"
        style={{
          display: "inline-block",
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: "#10b981",
          marginLeft: 6,
        }}
      />
    )}
  </NavLink>
);

const DealLayout = () => {
  const { dealId: dealIdParam } = useParams<{ dealId: string }>();
  const dealId = dealIdParam ? Number(dealIdParam) : undefined;
  const { data, isLoading, isError, error } = useDealDetail(dealId);
  // Role gates for left-nav items. Legacy renders a different sidebar
  // layout for Daymon users (vendor-side) — internal-only navigation
  // targets (Signatures, Errors, Fees, Promo Groups, Link) are hidden
  // since Daymon users don't have edit rights on those surfaces.
  // External users (vendor portal) also lose those items, mirroring the
  // legacy `pnlInternalOnly` panel pattern.
  // See audit/15-permission-model.md §3 (line 94) +
  // audit/11-aspxcs-and-business-nuances.md §4.
  const { data: currentUser } = useCurrentUser();
  const isDaymonUser = currentUser?.isDaymonUser ?? false;
  const isExternalUser = currentUser?.isExternalUser ?? false;
  // Treat Daymon and external users the same way the legacy
  // `pnlInternalOnly` panel does — hide internal-only nav items.
  const showInternalOnlyNav = !isDaymonUser && !isExternalUser;

  // Prefetch counts for the left-nav badges — these are cheap queries and
  // each badge gives the user an at-a-glance signal that mirrors legacy.
  const items = useDealItems(dealId);
  const notes = useDealNotes(dealId);
  const history = useDealHistory(dealId);
  const signatures = useDealSignatures(dealId);
  const errors = useDealErrors(dealId);
  const fees = useDealFees(dealId);
  const attachments = useDealAttachments(dealId);
  // DealReward is 1:1 with Deal — see audit on
  // ContentsAndActions.ascx.cs:161 / DealReward.GetByDealId(). We only
  // surface a "configured" dot on the Reward nav item when the row exists;
  // we never show a count badge. The nav item itself is gated on the
  // deal's isRewardPromotion flag.
  const reward = useDealReward(dealId);
  const promoGroups = useDealPromoGroups(dealId);

  if (dealId === undefined || Number.isNaN(dealId)) {
    return <p>Invalid deal id.</p>;
  }
  if (isLoading) return <p>Loading deal {dealId}…</p>;
  if (isError) {
    // CheckForPermissions 403 — the API blocks the user from even seeing
    // this deal (banner / vendor scope mismatch). Show a friendly state
    // instead of the generic error banner; route them back to the
    // dashboard so they don't keep banging on the door.
    if (isDealForbidden(error)) {
      return (
        <div className={s.errorBanner} role="alert">
          <p style={{ margin: 0, fontWeight: 600 }}>
            You don't have permission to view deal {dealId}.
          </p>
          <p style={{ margin: "0.5rem 0 0", fontSize: "0.9rem" }}>
            This deal is outside your banner / vendor scope. If you believe
            you should have access, contact a deal-management admin.
          </p>
          <p style={{ margin: "0.75rem 0 0" }}>
            <Link to="/" style={{ color: "#0a3d62" }}>← Back to dashboard</Link>
          </p>
        </div>
      );
    }
    const msg = error instanceof Error ? error.message : "unknown";
    return (
      <div className={s.errorBanner}>
        Could not load deal {dealId}: {msg}
      </div>
    );
  }
  if (!data) return <p>Deal {dealId} not found.</p>;

  return (
    <div className={s.wrap}>
      <DealHeader deal={data} />

      <div className={s.body}>
        <aside className={s.sidebar}>
          <h3 className={s.sidebarHeading}>Contents</h3>
          <NavItem to={`/dm/deals/${dealId}`} label="Header" end />
          <NavItem to={`/dm/deals/${dealId}/items`} label="Items" count={items.data?.length} />
          {data.isRewardPromotion && (
            <NavItem
              to={`/dm/deals/${dealId}/reward`}
              label="Reward"
              dot={reward.data != null}
            />
          )}
          <NavItem to={`/dm/deals/${dealId}/notes`} label="Notes" count={notes.data?.length} />
          <NavItem to={`/dm/deals/${dealId}/attachments`} label="Attachments" count={attachments.data?.length} />
          <NavItem to={`/dm/deals/${dealId}/history`} label="History" count={history.data?.length} />
          {/* Internal-only nav items — hidden for Daymon + external users,
              mirroring the legacy left-rail layout where vendor-side users
              get a slimmer Contents list (see audit 15 §3). */}
          {showInternalOnlyNav && (
            <>
              <NavItem to={`/dm/deals/${dealId}/signatures`} label="Signatures" count={signatures.data?.length} />
              <NavItem to={`/dm/deals/${dealId}/errors`} label="Errors" count={errors.data?.length} />
              <NavItem to={`/dm/deals/${dealId}/fees`} label="Fees" count={fees.data?.length} />
              <NavItem to={`/dm/deals/${dealId}/promo-groups`} label="Promo Groups" count={promoGroups.data?.length} />
              <NavItem to={`/dm/deals/${dealId}/link`} label="Link" />
            </>
          )}
          <ActionsPanel deal={data} />
        </aside>
        <section className={s.content}>
          <Outlet context={{ deal: data }} />
        </section>
      </div>
    </div>
  );
};

export default DealLayout;

/** Convenience hook for child routes to access the loaded deal. */
import { useOutletContext } from "react-router";
// eslint-disable-next-line react-refresh/only-export-components
export const useDealContext = () =>
  useOutletContext<{ deal: DealDetail }>().deal;
